import { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  Building2,
  FileBarChart,
  MapPin,
  Search,
  X,
  Zap,
} from "lucide-react";
import type { SearchResult } from "@syllarys/contracts";
import { apiClient } from "../state/EmsContext";

const TYPE_LABELS: Record<SearchResult["type"], string> = {
  company: "Entreprises",
  site: "Sites",
  delivery_point: "Points de livraison",
  anomaly: "Anomalies",
  report: "Rapports",
};

const TYPE_ICONS = {
  company: Building2,
  site: MapPin,
  delivery_point: Zap,
  anomaly: Activity,
  report: FileBarChart,
} as const;

function isEditableTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.matches("input, textarea, select") || target.isContentEditable)
  );
}

interface GlobalSearchProps {
  brandId: string;
  onSelect: (result: SearchResult) => void;
}

export function GlobalSearch({
  brandId,
  onSelect,
}: GlobalSearchProps): React.ReactNode {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<SearchResult[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const requestSerial = useRef(0);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent): void => {
      if (
        event.key.toLocaleLowerCase("fr-FR") !== "k" ||
        (!event.metaKey && !event.ctrlKey) ||
        isEditableTarget(event.target)
      )
        return;
      event.preventDefault();
      setOpen(true);
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open || query.trim().length < 2) return;
    const controller = new AbortController();
    const serial = requestSerial.current;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError("");
      void apiClient
        .searchBrand(
          brandId,
          { q: query.trim(), limit: 20 },
          { signal: controller.signal },
        )
        .then((page) => {
          if (controller.signal.aborted || serial !== requestSerial.current)
            return;
          setItems(page.items);
          setNextCursor(page.nextCursor);
          setActiveIndex(0);
        })
        .catch((reason: unknown) => {
          if (controller.signal.aborted) return;
          setItems([]);
          setNextCursor(undefined);
          setError(
            reason instanceof Error
              ? reason.message
              : "La recherche n’est pas disponible.",
          );
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 275);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [brandId, open, query]);

  const groups = useMemo(() => {
    const result = new Map<SearchResult["type"], SearchResult[]>();
    for (const item of items) {
      const group = result.get(item.type) ?? [];
      group.push(item);
      result.set(item.type, group);
    }
    return result;
  }, [items]);

  async function loadMore(): Promise<void> {
    if (!nextCursor || loadingMore) return;
    const serial = requestSerial.current;
    setLoadingMore(true);
    setError("");
    try {
      const page = await apiClient.searchBrand(brandId, {
        q: query.trim(),
        limit: 20,
        cursor: nextCursor,
      });
      if (serial !== requestSerial.current) return;
      setItems((current) => [...current, ...page.items]);
      setNextCursor(page.nextCursor);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "La suite des résultats n’est pas disponible.",
      );
    } finally {
      setLoadingMore(false);
    }
  }

  function choose(result: SearchResult): void {
    onSelect(result);
    closeSearch();
  }

  function closeSearch(): void {
    requestSerial.current += 1;
    setOpen(false);
    setQuery("");
    setItems([]);
    setNextCursor(undefined);
    setLoading(false);
    setError("");
  }

  return (
    <>
      <button
        type="button"
        className="global-search-trigger"
        aria-label="Recherche globale"
        aria-keyshortcuts="Meta+K Control+K"
        onClick={() => setOpen(true)}
      >
        <Search size={17} aria-hidden="true" />
        <span>Rechercher</span>
        <kbd>⌘K</kbd>
      </button>
      {open ? (
        <div className="global-search-backdrop" role="presentation">
          <button
            type="button"
            className="global-search-dismiss"
            aria-label="Fermer la recherche"
            onClick={closeSearch}
          />
          <section
            className="global-search-dialog"
            role="dialog"
            tabIndex={-1}
            aria-modal="true"
            aria-labelledby="global-search-title"
          >
            <header>
              <div>
                <p className="eyebrow">RECHERCHE AUTORISÉE</p>
                <h2 id="global-search-title">Retrouver un élément</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                aria-label="Fermer la recherche"
                onClick={closeSearch}
              >
                <X aria-hidden="true" />
              </button>
            </header>
            <label className="global-search-input">
              <Search size={18} aria-hidden="true" />
              <input
                ref={inputRef}
                role="combobox"
                aria-controls="global-search-results"
                aria-expanded={items.length > 0}
                aria-activedescendant={
                  items[activeIndex]
                    ? `global-search-${items[activeIndex].type}-${items[activeIndex].id}`
                    : undefined
                }
                value={query}
                placeholder="Entreprise, adresse, PRM/PCE, anomalie ou rapport"
                onKeyDown={(event) => {
                  if (event.key === "Escape") closeSearch();
                  if (event.key === "ArrowDown" && items.length) {
                    event.preventDefault();
                    setActiveIndex((index) => (index + 1) % items.length);
                  }
                  if (event.key === "ArrowUp" && items.length) {
                    event.preventDefault();
                    setActiveIndex(
                      (index) => (index - 1 + items.length) % items.length,
                    );
                  }
                  if (event.key === "Enter" && items[activeIndex]) {
                    event.preventDefault();
                    choose(items[activeIndex]);
                  }
                }}
                onChange={(event) => {
                  const value = event.target.value;
                  requestSerial.current += 1;
                  setQuery(value);
                  if (value.trim().length < 2) {
                    setItems([]);
                    setNextCursor(undefined);
                    setLoading(false);
                    setError("");
                  }
                }}
              />
            </label>
            <div
              className="global-search-results"
              id="global-search-results"
              role="listbox"
              aria-busy={loading}
            >
              {query.trim().length < 2 ? (
                <p className="global-search-state">
                  Saisissez au moins 2 caractères. Seuls les éléments de votre
                  périmètre sont recherchés.
                </p>
              ) : loading ? (
                <p className="global-search-state" role="status">
                  Recherche en cours…
                </p>
              ) : error ? (
                <p className="global-search-state form-error" role="alert">
                  {error}
                </p>
              ) : items.length === 0 ? (
                <p className="global-search-state">
                  Aucun résultat autorisé pour cette recherche.
                </p>
              ) : (
                [...groups.entries()].map(([type, group]) => (
                  <section key={type} aria-label={TYPE_LABELS[type]}>
                    <h3>{TYPE_LABELS[type]}</h3>
                    {group.map((item) => {
                      const Icon = TYPE_ICONS[item.type];
                      const index = items.indexOf(item);
                      return (
                        <button
                          type="button"
                          id={`global-search-${item.type}-${item.id}`}
                          role="option"
                          aria-selected={index === activeIndex}
                          className={index === activeIndex ? "is-active" : ""}
                          key={`${item.type}-${item.id}`}
                          onMouseEnter={() => setActiveIndex(index)}
                          onClick={() => choose(item)}
                        >
                          <Icon size={18} aria-hidden="true" />
                          <span>
                            <strong>{item.label}</strong>
                            <small>{item.description}</small>
                            <small>{item.context}</small>
                          </span>
                        </button>
                      );
                    })}
                  </section>
                ))
              )}
            </div>
            {nextCursor ? (
              <button
                type="button"
                className="secondary-button full"
                disabled={loadingMore}
                onClick={() => void loadMore()}
              >
                {loadingMore ? "Chargement…" : "Afficher plus de résultats"}
              </button>
            ) : null}
          </section>
        </div>
      ) : null}
    </>
  );
}
